import { z } from 'zod';
import type { ChecklistId, ChecklistRunId } from './checklists';
import { startOfDayInTz } from './wallTime';

/**
 * Agenda e período dos checklists operacionais (`MOD_CHECKLISTS`): a regra
 * pura que diz **qual execução vale agora**, qual é o id do documento dela e
 * quando ela atrasa.
 *
 * Origem: #336 — 2026-10-19 22:53 +0200 (F1 do módulo Checklists, §5.3–§5.4).
 *
 * Por quê: o id da execução é determinístico (`<checklistId>__<periodKey>`)
 * para que dois aparelhos offline no mesmo período escrevam no MESMO doc, sem
 * geração antecipada pelo servidor (§5.4). Isso só funciona se TS, Kotlin e
 * Swift calcularem o mesmo `periodKey` para o mesmo instante e fuso — por isso
 * a regra mora aqui, uma vez, e os espelhos são conferidos contra os vetores
 * gerados destas funções (`contract/cases/checklist-schedule.json`).
 *
 * Contrato: funções puras sobre `(schedule, instante em epoch-ms, fuso IANA)`.
 * Nenhuma lê relógio: o "agora" persistido vem do chamador (Android:
 * `TrustedClock`; servidor: hora do evento). Fuso inválido LANÇA — o chamador
 * resolve o fuso do tenant antes (`resolveTenantTimeZone` no servidor,
 * `TenantDayWindow` no Android); decidir período no fuso errado criaria um doc
 * de execução no dia errado, que nenhuma releitura corrige.
 *
 * Semântica (decisão 21 do owner na sessão da F1, 2026-10-19):
 *  - DAILY: período = o dia no fuso do tenant; `weekdays` diz em que dias da
 *    semana ocorre (vazio = todos). Vence às `dueMinuteOfDay` (nulo = fim do dia).
 *  - SHIFT: um período por turno; turno que cruza a meia-noite pertence ao dia
 *    de INÍCIO (`2026-09-03__NIGHT` vai das 22:00 do dia 3 às 06:00 do dia 4).
 *    Vence no fim do turno. `weekdays` filtra os dias, como no DAILY.
 *  - WEEKLY: período = semana ISO (segunda a domingo, ano da semana ISO);
 *    `weekdays` tem exatamente 1 dia, o do vencimento. Pode ser feita em
 *    qualquer dia da semana.
 *  - MONTHLY: período = o mês; vence no `dayOfMonth` (1–28, para existir em
 *    todo mês). Pode ser feita em qualquer dia do mês.
 *  - ON_DEMAND: sem período; cada execução tem id próprio (`onDemandRunId`).
 *  - Atrasada = `agora > dueAtMillis + graceMinutes`. `periodEndMillis` é o
 *    último ms do período (inclusivo), como `endOfDayInTz`.
 *
 * Usado por: (F1) o gerador de vetores `scripts/gen-contract-snapshot.ts`
 * (`cases/checklist-schedule.json`) e o espelho Kotlin
 * `apps/android/.../domain/checklist/ChecklistSchedule.kt`, conferido pelo
 * `ChecklistScheduleTest`. Previstos: lista do dia no Android (F3), crons de
 * `MISSED` e de atraso nas Functions (F5), espelho Swift (F6), web (F7).
 *
 * Arquitetura: tempo = server > GPS > sistema (CLAUDE.md). Esta regra não
 * escolhe a fonte; ela só converte instante → período. O trigger compara o
 * `periodKey` gravado com a hora do servidor e marca `periodDrift` (F2) — alerta,
 * nunca correção silenciosa (#336 §9).
 *
 * Falhas conhecidas:
 *  - 2026-10-19 (#336, auditoria da F1): em fuso com horário de verão
 *    começando à MEIA-NOITE (America/Santiago, America/Havana), a hora
 *    inexistente 00:30 voltava para o dia anterior em vez de avançar, e o TS
 *    divergia do Kotlin. Ver `instantAt`.
 *  - Parente: #241 — "amanhã" e "fim do dia" como `+24h` erravam 1 h na
 *    troca de horário; aqui todo limite de dia vem de `startOfDayInTz`
 *    (primeiro instante do dia-calendário).
 *
 * Dicas: mexeu na regra → `npm run contract` regenera os vetores, e o
 * `ChecklistScheduleTest` do Android (e o Swift da F6) passa a falhar até o
 * espelho acompanhar. Esse é o gate da F1; não "conserte" o vetor à mão.
 * O `RUN_ID_PATTERN` é copiado à mão para `firestore.rules` (F2).
 *
 * Issues: #336 (épico e decisão 21), #241 (dia-calendário no DST).
 *
 * Histórico:
 *  - 2026-10-19 22:53 +0200 — (#336) F1: agenda, período e ids.
 *  - 2026-10-19 23:22 +0200 — (#336) auditoria da F1: gap de DST à
 *    meia-noite resolvido como o `java.time`; vetores de Santiago e Havana.
 */

// ------------------------------------------------------------------ enums

/** Frequência da agenda de um checklist (#336 decisão 4). */
export const ChecklistFrequency = {
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
  SHIFT: 'SHIFT',
  ON_DEMAND: 'ON_DEMAND',
} as const;
export type ChecklistFrequency = (typeof ChecklistFrequency)[keyof typeof ChecklistFrequency];
export const ALL_CHECKLIST_FREQUENCIES = Object.values(ChecklistFrequency) as [
  ChecklistFrequency,
  ...ChecklistFrequency[],
];
export const ChecklistFrequencySchema = z.enum(ALL_CHECKLIST_FREQUENCIES);

// ------------------------------------------------------------------ schema

/** Minutos num dia: 0 = 00:00, 1439 = 23:59. */
export const MINUTES_PER_DAY = 1440;

/**
 * Chave de turno: `MORNING`, `H08`, `NIGHT_2`. Sem `__` de propósito: o `__`
 * separa as partes do `runId`, e uma chave com ele tornaria o id ambíguo.
 */
export const CHECKLIST_SHIFT_KEY_PATTERN = /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/;
/** Teto da chave de turno — cabe no id do doc sem chegar perto de 1500 bytes. */
export const CHECKLIST_SHIFT_KEY_MAX = 24;
/** Teto de turnos por dia: a medição do balcão a cada 2 h (CVS 3/2026 art. 73) dá 12. */
export const CHECKLIST_SHIFTS_MAX = 12;
/** Teto da carência, em minutos (um dia). */
export const CHECKLIST_GRACE_MINUTES_MAX = MINUTES_PER_DAY;

const MinuteOfDaySchema = z.number().int().min(0).max(MINUTES_PER_DAY - 1);

/**
 * Turno: janela `[startMinute, endMinute)` no fuso do tenant. `endMinute <=
 * startMinute` significa que o turno cruza a meia-noite; igual seria um turno
 * de 24 h, recusado (use DAILY).
 */
export const ChecklistShiftSchema = z
  .object({
    key: z.string().min(1).max(CHECKLIST_SHIFT_KEY_MAX).regex(CHECKLIST_SHIFT_KEY_PATTERN),
    startMinute: MinuteOfDaySchema,
    endMinute: MinuteOfDaySchema,
  })
  .refine((s) => s.startMinute !== s.endMinute, {
    message: 'turno com início igual ao fim',
    path: ['endMinute'],
  });
export type ChecklistShift = z.infer<typeof ChecklistShiftSchema>;

/**
 * Agenda de um checklist (`tenants/{tid}/checklists/{id}.schedule` e
 * `defaultSchedule` dos modelos da biblioteca).
 *
 * Dados — exemplo (medição de geladeira 2×/dia):
 *   { "frequency": "SHIFT", "weekdays": [], "dayOfMonth": null,
 *     "shifts": [{ "key": "MORNING", "startMinute": 360, "endMinute": 840 },
 *                { "key": "EVENING", "startMinute": 840, "endMinute": 1380 }],
 *     "dueMinuteOfDay": null, "graceMinutes": 60 }
 *
 * `weekdays` é ISO (1 = segunda … 7 = domingo). Todos os campos são
 * obrigatórios no wire — nada de default — para o doc gravado pelo Android,
 * pelo iOS e pelo web ser o mesmo byte a byte.
 */
export const ChecklistScheduleSchema = z
  .object({
    frequency: ChecklistFrequencySchema,
    weekdays: z.array(z.number().int().min(1).max(7)).max(7),
    dayOfMonth: z.number().int().min(1).max(28).nullable(),
    shifts: z.array(ChecklistShiftSchema).max(CHECKLIST_SHIFTS_MAX),
    dueMinuteOfDay: MinuteOfDaySchema.nullable(),
    graceMinutes: z.number().int().min(0).max(CHECKLIST_GRACE_MINUTES_MAX),
  })
  .superRefine((s, ctx) => {
    const issue = (path: string, message: string): void => {
      ctx.addIssue({ code: 'custom', path: [path], message });
    };
    if (new Set(s.weekdays).size !== s.weekdays.length) issue('weekdays', 'dia repetido');
    if (new Set(s.shifts.map((sh) => sh.key)).size !== s.shifts.length) issue('shifts', 'turno repetido');
    const usesShifts = s.frequency === ChecklistFrequency.SHIFT;
    if (usesShifts !== s.shifts.length > 0) issue('shifts', 'turnos só (e sempre) na frequência SHIFT');
    if ((s.frequency === ChecklistFrequency.MONTHLY) !== (s.dayOfMonth !== null)) {
      issue('dayOfMonth', 'dayOfMonth só (e sempre) na frequência MONTHLY');
    }
    // Decisão 21: a semanal vence num dia só; a mensal e a sob demanda não usam dias.
    if (s.frequency === ChecklistFrequency.WEEKLY && s.weekdays.length !== 1) {
      issue('weekdays', 'semanal exige exatamente 1 dia');
    }
    if (
      (s.frequency === ChecklistFrequency.MONTHLY || s.frequency === ChecklistFrequency.ON_DEMAND) &&
      s.weekdays.length > 0
    ) {
      issue('weekdays', 'mensal e sob demanda não usam dias da semana');
    }
    // O turno vence no próprio fim; sob demanda não vence.
    if (
      (s.frequency === ChecklistFrequency.SHIFT || s.frequency === ChecklistFrequency.ON_DEMAND) &&
      s.dueMinuteOfDay !== null
    ) {
      issue('dueMinuteOfDay', 'turno e sob demanda não têm horário de vencimento');
    }
  });
export type ChecklistSchedule = z.infer<typeof ChecklistScheduleSchema>;

// ------------------------------------------------------------------ período

/**
 * Uma ocorrência da agenda: o período que um doc de execução cobre.
 * `shiftKey` só na frequência SHIFT.
 */
export type ChecklistOccurrence = {
  readonly periodKey: string;
  readonly shiftKey: string | null;
  readonly periodStartMillis: number;
  /** Último ms do período (inclusivo). */
  readonly periodEndMillis: number;
  readonly dueAtMillis: number;
};

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 24 * 60 * 60 * 1000;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function parseDate(date: string): { y: number; m: number; d: number } {
  const match = DATE_RE.exec(date);
  if (!match) throw new Error(`data inválida: '${date}'`);
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}

function formatUtcDate(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCFullYear())}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/**
 * `'YYYY-MM-DD'` deslocada `days` dias-calendário (aritmética UTC pura, sem fuso).
 * Exportada na #336 F5: o cron de fechamento varre os dias recentes do tenant.
 */
export function addDays(date: string, days: number): string {
  const { y, m, d } = parseDate(date);
  return formatUtcDate(Date.UTC(y, m - 1, d) + days * DAY_MS);
}

/** Dia da semana ISO (1 = segunda … 7 = domingo) de uma data-calendário. */
function isoWeekday(date: string): number {
  const { y, m, d } = parseDate(date);
  const sundayZero = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return sundayZero === 0 ? 7 : sundayZero;
}

/**
 * Semana ISO 8601 da data: a semana pertence ao ano da sua quinta-feira. Por
 * isso 2027-01-01 (sexta) é `2026-W53`, e não `2027-W00`.
 */
function isoWeekKey(date: string): string {
  const thursday = addDays(date, 4 - isoWeekday(date));
  const { y } = parseDate(thursday);
  const jan1 = Date.UTC(y, 0, 1);
  const { m, d } = parseDate(thursday);
  const week = Math.floor((Date.UTC(y, m - 1, d) - jan1) / DAY_MS / 7) + 1;
  return `${String(y)}-W${pad2(week)}`;
}

/** Data-calendário (`YYYY-MM-DD`) do instante no fuso; lança com fuso inválido. Exportada na #336 F5 (cron de fechamento). */
export function dateInTz(ms: number, timeZone: string): string {
  return wallMinuteInTz(ms, timeZone).slice(0, 10);
}

// Intl.DateTimeFormat é caro de construir e a lista do dia chama isto por
// checklist; um formatter por fuso basta (mesmo motivo do cache de `wallTime.ts`).
const minuteFormatters = new Map<string, Intl.DateTimeFormat>();

/** `'YYYY-MM-DD HH:mm'` do instante no fuso; lança `RangeError` com fuso inválido. */
function wallMinuteInTz(ms: number, timeZone: string): string {
  let fmt = minuteFormatters.get(timeZone);
  if (!fmt) {
    // `h23`: 'h24' devolveria "24" à meia-noite.
    fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    minuteFormatters.set(timeZone, fmt);
  }
  const parts = fmt.formatToParts(new Date(ms));
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;
}

/** Primeiro instante do dia-calendário no fuso (#241); lança se não der. */
function startOf(date: string, timeZone: string): number {
  const ms = startOfDayInTz(date, timeZone);
  if (ms === null) throw new Error(`fuso inválido ou data fora do alcance: '${timeZone}' ${date}`);
  return ms;
}

/**
 * Instante da hora de parede `minute` na data, no fuso. Minuto 0 é o início
 * do dia-calendário (que no gap de DST à meia-noite é 01:00, como o
 * `atStartOfDay` do java.time). Hora inexistente no gap avança pelo tamanho do
 * gap; hora ambígua no overlap fica no offset ANTERIOR — a resolução padrão do
 * `ZonedDateTime.of` do Kotlin, conferida pelos vetores.
 */
function instantAt(date: string, minute: number, timeZone: string): number {
  if (minute === 0) return startOf(date, timeZone);
  // Auditoria 2026-10-19 23:22 +0200 (#336, F1): a versão anterior pegava o
  // resultado do `wallTimeToEpoch` e só corrigia o overlap. No gap ela
  // dependia do ponto fixo que a iteração do `wallTimeToEpoch` atingia: em Roma
  // (gap às 02:00) avançava, mas em fuso com gap À MEIA-NOITE (America/Santiago,
  // America/Havana) 00:30 voltava para 23:30 do DIA ANTERIOR, enquanto o
  // `ZonedDateTime.of` do Kotlin dá 01:30. Prazo e início de turno divergiam
  // entre plataformas, e o turno 23:30→00:30 ficava com período vazio
  // (fim < início). Agora a resolução é a do `java.time`, explícita: o offset
  // de ANTES da transição vale se a hora existe com ele (inclui o overlap) ou
  // se a hora não existe (gap: avança pelo tamanho do gap); senão vale o de
  // depois. Vetores de Santiago/Havana em `cases/checklist-schedule.json`.
  const desired = `${date} ${pad2(Math.floor(minute / 60))}:${pad2(minute % 60)}`;
  const { y, m, d } = parseDate(date);
  const desiredAsUtc = Date.UTC(y, m - 1, d, Math.floor(minute / 60), minute % 60);
  // ±1 dia basta: offsets cabem em ±14 h e duas transições nunca distam < 2 dias.
  const before = desiredAsUtc - offsetAt(desiredAsUtc - DAY_MS, timeZone);
  if (wallMinuteInTz(before, timeZone) === desired) return before;
  const after = desiredAsUtc - offsetAt(desiredAsUtc + DAY_MS, timeZone);
  return wallMinuteInTz(after, timeZone) === desired ? after : before;
}

/** Offset do fuso (ms, hora de parede − UTC) no instante; resolução de minuto. */
function offsetAt(ms: number, timeZone: string): number {
  const wall = wallMinuteInTz(ms, timeZone);
  const { y, m, d } = parseDate(wall.slice(0, 10));
  const wallAsUtc = Date.UTC(y, m - 1, d, Number(wall.slice(11, 13)), Number(wall.slice(14, 16)));
  return wallAsUtc - Math.floor(ms / 60_000) * 60_000;
}

function activeOn(schedule: ChecklistSchedule, date: string): boolean {
  return schedule.weekdays.length === 0 || schedule.weekdays.includes(isoWeekday(date));
}

function dueOn(schedule: ChecklistSchedule, date: string, timeZone: string): number {
  return schedule.dueMinuteOfDay === null
    ? startOf(addDays(date, 1), timeZone)
    : instantAt(date, schedule.dueMinuteOfDay, timeZone);
}

function shiftOccurrence(shift: ChecklistShift, date: string, timeZone: string): ChecklistOccurrence {
  const start = instantAt(date, shift.startMinute, timeZone);
  const endDate = shift.endMinute <= shift.startMinute ? addDays(date, 1) : date;
  const end = instantAt(endDate, shift.endMinute, timeZone);
  return {
    periodKey: `${date}__${shift.key}`,
    shiftKey: shift.key,
    periodStartMillis: start,
    periodEndMillis: end - 1,
    dueAtMillis: end,
  };
}

function weekOccurrence(schedule: ChecklistSchedule, date: string, timeZone: string): ChecklistOccurrence {
  const monday = addDays(date, 1 - isoWeekday(date));
  const dueDay = addDays(monday, (schedule.weekdays[0] ?? 7) - 1);
  return {
    periodKey: isoWeekKey(date),
    shiftKey: null,
    periodStartMillis: startOf(monday, timeZone),
    periodEndMillis: startOf(addDays(monday, 7), timeZone) - 1,
    dueAtMillis: dueOn(schedule, dueDay, timeZone),
  };
}

function monthOccurrence(schedule: ChecklistSchedule, date: string, timeZone: string): ChecklistOccurrence {
  const { y, m } = parseDate(date);
  const first = `${String(y)}-${pad2(m)}-01`;
  const nextFirst = m === 12 ? `${String(y + 1)}-01-01` : `${String(y)}-${pad2(m + 1)}-01`;
  return {
    periodKey: `${String(y)}-${pad2(m)}`,
    shiftKey: null,
    periodStartMillis: startOf(first, timeZone),
    periodEndMillis: startOf(nextFirst, timeZone) - 1,
    dueAtMillis: dueOn(schedule, `${String(y)}-${pad2(m)}-${pad2(schedule.dayOfMonth ?? 1)}`, timeZone),
  };
}

/**
 * Ocorrências do dia-calendário `date` (`YYYY-MM-DD`, no fuso do tenant): o
 * que a lista do dia mostra para aquele dia.
 *
 *  - DAILY: a do dia, se o dia da semana está ativo.
 *  - SHIFT: os turnos que COMEÇAM no dia, na ordem da agenda. O turno da
 *    noite anterior que ainda corre de madrugada é do dia anterior — quem quer
 *    "o que está aberto agora" usa `occurrenceAt`.
 *  - WEEKLY/MONTHLY: a do período que contém o dia, em TODO dia do período
 *    (a semanal fica pendente a semana inteira). Quem conta "programadas no
 *    dia" (agregado da F5) filtra por `dueAtMillis` dentro do dia.
 *  - ON_DEMAND: nenhuma.
 */
export function occurrencesForDay(
  schedule: ChecklistSchedule,
  date: string,
  timeZone: string,
): ChecklistOccurrence[] {
  parseDate(date);
  switch (schedule.frequency) {
    case ChecklistFrequency.DAILY:
      return activeOn(schedule, date)
        ? [
            {
              periodKey: date,
              shiftKey: null,
              periodStartMillis: startOf(date, timeZone),
              periodEndMillis: startOf(addDays(date, 1), timeZone) - 1,
              dueAtMillis: dueOn(schedule, date, timeZone),
            },
          ]
        : [];
    case ChecklistFrequency.SHIFT:
      return activeOn(schedule, date) ? schedule.shifts.map((s) => shiftOccurrence(s, date, timeZone)) : [];
    case ChecklistFrequency.WEEKLY:
      return [weekOccurrence(schedule, date, timeZone)];
    case ChecklistFrequency.MONTHLY:
      return [monthOccurrence(schedule, date, timeZone)];
    case ChecklistFrequency.ON_DEMAND:
      return [];
  }
}

/**
 * A ocorrência cujo período contém o instante, ou `null` (sob demanda, dia
 * inativo, ou fora de qualquer turno). No SHIFT confere primeiro os turnos do
 * dia anterior que cruzam a meia-noite — às 02:00 do dia 4, o turno aberto é
 * `2026-09-03__NIGHT` — e, havendo sobreposição, vale o primeiro da agenda.
 */
export function occurrenceAt(
  schedule: ChecklistSchedule,
  nowMillis: number,
  timeZone: string,
): ChecklistOccurrence | null {
  const today = dateInTz(nowMillis, timeZone);
  const contains = (o: ChecklistOccurrence): boolean =>
    o.periodStartMillis <= nowMillis && nowMillis <= o.periodEndMillis;
  if (schedule.frequency === ChecklistFrequency.SHIFT) {
    const candidates = [
      ...occurrencesForDay(schedule, addDays(today, -1), timeZone),
      ...occurrencesForDay(schedule, today, timeZone),
    ];
    return candidates.find(contains) ?? null;
  }
  return occurrencesForDay(schedule, today, timeZone).find(contains) ?? null;
}

/**
 * `periodKey` da execução que vale no instante: `2026-09-03`,
 * `2026-09-03__MORNING`, `2026-W36` ou `2026-09`. `null` quando nenhuma
 * ocorrência contém o instante (ver `occurrenceAt`).
 */
export function periodKeyFor(schedule: ChecklistSchedule, nowMillis: number, timeZone: string): string | null {
  return occurrenceAt(schedule, nowMillis, timeZone)?.periodKey ?? null;
}

/** Atrasada: passou do prazo mais a carência. No limite exato ainda não está. */
export function isOverdue(occurrence: ChecklistOccurrence, graceMinutes: number, nowMillis: number): boolean {
  return nowMillis > occurrence.dueAtMillis + graceMinutes * 60 * 1000;
}

// ------------------------------------------------------------------ ids

/**
 * Id de checklist aceito no `runId`: o auto-id do Firestore (20 alfanuméricos)
 * ou qualquer id alfanumérico até 64. Sem `_`, para o `__` do `runId` nunca
 * aparecer dentro do id.
 */
export const CHECKLIST_ID_PATTERN = /^[A-Za-z0-9]{1,64}$/;

/** Separador das partes do `runId` e do `periodKey` de turno. */
export const CHECKLIST_RUN_ID_SEPARATOR = '__';
/** Marcador da execução sob demanda: `<checklistId>__OD__<uuid>`. */
export const CHECKLIST_ON_DEMAND_MARKER = 'OD';

/**
 * Formato de todo `runId` válido (#336 §5.4). Copiado à mão para a rule de
 * `checklistRuns` na F2 — a rule usa RE2 com casamento inteiro, e esta regex
 * não usa nada que o RE2 não tenha.
 */
export const RUN_ID_PATTERN =
  /^[A-Za-z0-9]{1,64}__(?:\d{4}-\d{2}-\d{2}(?:__[A-Z0-9]+(?:_[A-Z0-9]+)*)?|\d{4}-W\d{2}|\d{4}-\d{2}|OD__[A-Za-z0-9-]{8,64})$/;

function requireChecklistId(checklistId: string): void {
  if (!CHECKLIST_ID_PATTERN.test(checklistId)) throw new Error(`checklistId inválido para runId: '${checklistId}'`);
}

/** Id do doc da execução agendada: `<checklistId>__<periodKey>`. */
export function runDocId(checklistId: ChecklistId | string, periodKey: string): ChecklistRunId {
  requireChecklistId(checklistId);
  const id = `${checklistId}${CHECKLIST_RUN_ID_SEPARATOR}${periodKey}`;
  if (!RUN_ID_PATTERN.test(id)) throw new Error(`periodKey inválido: '${periodKey}'`);
  return id as ChecklistRunId;
}

/**
 * Id da execução sob demanda: `<checklistId>__OD__<uuid>`. O uuid é gerado no
 * aparelho, uma vez por "Iniciar" — cada execução sob demanda é um doc novo.
 */
export function onDemandRunId(checklistId: ChecklistId | string, uuid: string): ChecklistRunId {
  requireChecklistId(checklistId);
  const id = `${checklistId}${CHECKLIST_RUN_ID_SEPARATOR}${CHECKLIST_ON_DEMAND_MARKER}${CHECKLIST_RUN_ID_SEPARATOR}${uuid}`;
  if (!RUN_ID_PATTERN.test(id)) throw new Error(`uuid inválido para runId: '${uuid}'`);
  return id as ChecklistRunId;
}
